/*
討伐で出てくる相手 5 体。

**かめ / うさぎ / ふくろう と置き換えない。層が違う。** pace.ts の 3 匹は
「いま速いのか」を見せるペースの目盛りで、打っている最中ずっと動いている。
こちらは倒す相手で、HP が減る。片方を消すと、打っている最中に自分の速さを
読む手段か、60 秒打ち切ったあとの結末か、どちらかが無くなる
(docs/typing/battle-measurement-spec.md)。

## HP の根拠

**pace.ts の kpm と 60 秒から逆算している。** 60 秒に kpm ぶんの打鍵が入るので、
「その速さで 1 度も外さなかったとき」の総ダメージは noMissDamage(kpm) で出る。
HP はその 8 割に置いた。

8 割にしてあるのは、**誤打鍵でコンボが 0 に戻るぶん、実際の総ダメージは
無傷の計算より必ず下に出る**ため。10 割に置くと、その速さで打てる人でも
1 度も外せない相手になる。2 割の余裕が妥当かどうかは実際に打って
決めていない。手触りを見て動かす。

| | 根拠にした速さ | 無傷なら | 置いた HP |
|---|---|---|---|
| はじまりスライム | 80 (かめの手前) | 1,920 | 1,500 |
| ポジションゴーレム | 120 (かめ相当) | 3,120 | 2,500 |
| れんけいファントム | 180 (かめとうさぎの間) | 4,920 | 3,900 |
| コンボバット | 240 (うさぎ相当) | 6,720 | 5,400 |
| ロングワーム | 340 (ふくろう相当) | 9,720 | 7,800 |

数が合っているかは battle.test.ts が noMissDamage を呼んで確かめる。
表だけ直して値を直し忘れると落ちる。

## 絵

**まだ持たせない。** 素材が来ていないので image は null。存在しないパスを
先に書くと、画面が 404 の枠を並べた状態で「できている」ように見える。
*/

import type { Monster } from "./battle";

/*
弱い順に並べる。画面はこの順で出す。

**名前は Asana のプロジェクト説明が正本。** 初期モンスターとして
はじまりスライム / ポジションゴーレム / れんけいファントム / コンボバット /
ロングワーム が指定してある。勝手に別の名前を付けない。
*/
export const MONSTERS: readonly Monster[] = [
  {
    id: "hajimari",
    name: "はじまりスライム",
    // かめの手前 (80 KPM) x 60 秒ぶんの無傷ダメージ 1,920 の約 8 割
    maxHp: 1_500,
    caption: "はじめの1体。ゆっくりでも打ち切れば消える",
    referenceKpm: 80,
    image: null,
  },
  {
    id: "position",
    name: "ポジションゴーレム",
    // かめ (120 KPM) x 60 秒ぶんの無傷ダメージ 3,120 の約 8 割
    maxHp: 2_500,
    caption: "指の定位置が崩れると硬くなる。かめに並べば崩せる",
    referenceKpm: 120,
    image: null,
  },
  {
    id: "renkei",
    name: "れんけいファントム",
    // かめとうさぎの間 (180 KPM) x 60 秒ぶんの無傷ダメージ 4,920 の約 8 割
    maxHp: 3_900,
    caption: "1打ずつでは捉えられない。続けて当てるほど効く",
    referenceKpm: 180,
    image: null,
  },
  {
    id: "combo",
    name: "コンボバット",
    // うさぎ (240 KPM) x 60 秒ぶんの無傷ダメージ 6,720 の約 8 割
    maxHp: 5_400,
    caption: "外した瞬間に逃げる。うさぎの速さで追い詰める",
    referenceKpm: 240,
    image: null,
  },
  {
    id: "longworm",
    name: "ロングワーム",
    // ふくろう (340 KPM) x 60 秒ぶんの無傷ダメージ 9,720 の約 8 割
    maxHp: 7_800,
    caption: "長い。ふくろうに追いつく速さでないと削り切れない",
    referenceKpm: 340,
    image: null,
  },
];

/** HP を根拠の何倍に置いたか。表と実装の両方から参照する */
export const MONSTER_HP_MARGIN = 0.8;

export function monsterById(id: string): Monster | undefined {
  return MONSTERS.find((m) => m.id === id);
}

/**
 * 最初に出す相手。
 *
 * **いちばん弱いところから始める。** 初手で倒せないと、
 * 60 秒打ち切ったあとに何も起きないのと同じ画面になる
 */
export const FIRST_MONSTER: Monster = MONSTERS[0];

/**
 * 倒した相手の、1 つ上の相手。**いちばん上を倒したら null。**
 *
 * 結果の画面で「次へ」を出すかの判断にだけ使う。並びは MONSTERS の順
 * (弱い順) をそのまま読むので、並びを変えると次の相手も一緒に変わる。
 * 一覧に無い相手 (検査の的など) を渡しても null を返し、勝手に 1 体目へ戻さない
 */
export function nextMonster(current: Monster): Monster | null {
  const index = MONSTERS.findIndex((m) => m.id === current.id);
  if (index < 0) return null;
  return MONSTERS[index + 1] ?? null;
}
